import { Heilmittelbereich } from './heilmittelbereich.enum.js';
/**
 * Feld 16 `Heilmittelart` of the Muster-13 PDF417 barcode — the **authoritative**
 * source of a Verordnung's Heilmittelbereich (#1504).
 *
 * A Verordnung is by construction one Bereich, and the Bereich decides money: it
 * picks the Abrechnungs-IK (a `Leistungserbringer` is keyed
 * `OfficeLocation × Heilmittelbereich`) and the price lineage (`(Bereich,
 * Vertrag) → Linie`, with no fallback across lineages). Reading it off whichever
 * BillableServiceItems happen to hang on the plan derives the Verordnung from
 * its own consequences; this reads it from what the physician prescribed.
 *
 * ⚠ **The codes are not in the enum's declaration order.** `2` is Podologie and
 * `4` is Ergotherapie; the enum declares Ergotherapie second. A map written by
 * zipping `Object.values(Heilmittelbereich)` against `1..5` would price every
 * Podologie Verordnung as Ergotherapie — a wrong lineage, a wrong IK, and never
 * an exception. The pairs are therefore written out, one per line.
 */
const BEREICH_BY_HEILMITTELART = {
    '1': Heilmittelbereich.PHYSIOTHERAPY,
    '2': Heilmittelbereich.PODIATRY,
    '3': Heilmittelbereich.SPEECH_THERAPY,
    '4': Heilmittelbereich.OCCUPATIONAL_THERAPY,
    '5': Heilmittelbereich.NUTRITION_THERAPY,
};
/**
 * The five codes Feld 16 can carry, in the order the KBV publishes them.
 *
 * ⚠ Exported for the **reading** direction only — a caller that wants to know
 * what the closed set is. The inverse (Bereich → code) is not here: printing a
 * Bereich back into Feld 16 is serialisation, and serialisation is TM-9539's.
 * An unused inverse is an invitation to serialise against a map nobody's caller
 * has ever exercised.
 */
export const HEILMITTELART_CODES = Object.keys(BEREICH_BY_HEILMITTELART);
/**
 * The Bereich Feld 16 names, or `null` for anything outside the closed five.
 *
 * ⚠ Null rather than a throw, and null rather than a guess. The capture layer
 * warns and saves anyway (#1502 decision 8) — a Verordnung whose Feld 16 is
 * blank or unreadable is still a Verordnung, and refusing it here would hold up
 * a scan over a field a Therapeut:in can supply by hand. What must never happen
 * is a *substituted* Bereich: that is a wrong price lineage, silently.
 */
export function heilmittelbereichFromHeilmittelart(raw) {
    if (raw === null || raw === undefined)
        return null;
    return BEREICH_BY_HEILMITTELART[raw.trim()] ?? null;
}
/**
 * The warning a scan owes the Therapeut:in when Feld 16 carried something the
 * published set does not know — or `null` when there is nothing to say.
 *
 * ⚠ **A warning, and only ever a warning** (#1502 decision 8). The scan still
 * saves; the Verordnung simply has no Bereich yet, which is a question the
 * Verordnungsformular answers in seconds. Refusing the scan over it would be
 * stricter than the spec and would hold up everything else the barcode carried.
 *
 * ⚠ **A blank Feld 16 is not a problem.** Absence is the commonest shape of
 * "not stated", and warning on every blank would train people to click past the
 * banner that does matter.
 */
export function heilmittelartProblem(raw) {
    const trimmed = (raw ?? '').trim();
    if (trimmed === '')
        return null;
    if (heilmittelbereichFromHeilmittelart(trimmed) !== null)
        return null;
    return {
        code: 'HEILMITTELART_OUTSIDE_VALUE_SET',
        field: 'heilmittelart',
        value: trimmed,
        message: `Die Heilmittelart „${trimmed}“ (Feld 16) ist keine der bekannten Angaben ` +
            `${HEILMITTELART_CODES.join(', ')}. Der Heilmittelbereich bleibt offen und lässt sich ` +
            `auf der Verordnung nachtragen.`,
    };
}
//# sourceMappingURL=heilmittelart.js.map